import Cookies from 'js-cookie';

export const TOKEN_TYPE = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  EXPIRES_AT: 'tokenExpiresAt',
} as const;

export type TokenType = typeof TOKEN_TYPE[keyof typeof TOKEN_TYPE];

// Token management functions
export const setTokenToCookies = (
  tokenType: TokenType,
  token: string,
  expiresInSeconds?: number
): void => {
  const options: Cookies.CookieAttributes = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
  };

  if (expiresInSeconds) {
    options.expires = new Date(Date.now() + expiresInSeconds * 1000);
  } else {
    // Default expiry: 1 hour for access token, 30 days for refresh token
    const defaultDays = tokenType === TOKEN_TYPE.ACCESS_TOKEN ? 1/24 : 30;
    options.expires = defaultDays;
  }

  Cookies.set(tokenType, token, options);
};

export const getTokenFromCookies = (tokenType: TokenType): string | null => {
  return Cookies.get(tokenType) || null;
};

export const removeTokenFromCookies = (tokenType: TokenType): void => {
  Cookies.remove(tokenType, { path: '/' });
};

export const clearAllTokens = (): void => {
  removeTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
  removeTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN);
  removeTokenFromCookies(TOKEN_TYPE.EXPIRES_AT);
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
};

// Get all auth tokens
export const getAuthTokens = () => {
  return {
    accessToken: getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN),
    refreshToken: getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN),
    expiresAt: getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT),
  };
};

// Check if token is expired or will expire soon (30 seconds buffer)
export const isTokenExpiring = (): boolean => {
  const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT);
  if (!expiresAt) return true;

  const expireTime = new Date(expiresAt);
  const now = new Date();
  const bufferTime = new Date(now.getTime() + 30 * 1000); // 30 seconds buffer

  return bufferTime >= expireTime;
};

// Set token expiry time
export const setTokenExpiry = (expiresAt: string): void => {
  setTokenToCookies(TOKEN_TYPE.EXPIRES_AT, expiresAt);
};